// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.CategoryItem.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import React, { Component, PropTypes, cloneElement } from "react";
import connect from "share/enhancers/Izenda.Connect";
import Validator from "share/components/hoc/Izenda.Validator";
import OverflowText from "share/components/controls/Izenda.OverflowText";
import Input from "share/components/controls/Izenda.Input";
import { Link } from "react-router";
import uiState from "logic/Izenda.UiState";
import routeUtils from "share/utils/Izenda.RouteUtils";
import { CATEGORY_TREE } from "logic/data/Izenda.CategoryConstants";
import LayoutUtils from "share/utils/Izenda.LayoutUtils";
import { observable, action } from "mobx";
import Promise from "bluebird";
import { isNil, merge, get } from "lodash/fp";
import classnames from "classnames";
import config from "config";

const ValidatorInput = Validator()(Input);

@connect()
export default class CategoryItem extends Component {
  static displayName = "CategoryItem";

  static propTypes = {
    dataSource: PropTypes.object.isRequired,
    routeObject: PropTypes.object.isRequired,
    routeParams: PropTypes.object.isRequired,
    onLoadCategoryList: PropTypes.func,
    onValidateCategoryName: PropTypes.func,
    toggleIcon: PropTypes.object,
    isGlobalLocal: PropTypes.bool
  };

  @observable
  categoryState = {
    originalName: this.props.dataSource.name,
    editMode: false
  };

  componentWillReceiveProps(nextProps: any) {
    this.categoryState.originalName = nextProps.dataSource.name;
  }

  onSave = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();

    const { dataSource, dataSource: { id }, validation } = this.props;

    return validation.validate("name").then(
      action(success => {
        if (success) {
          this.categoryState.editMode = false;
          dataSource.updateIsEditing(false);
          return this.loadReportList(id);
        } else {
          dataSource.updateIsEditing(true);
          return Promise.reject();
        }
      })
    );
  };

  onEnter = (e: Event) => {
    if (e.key === "Enter") this.onSave(e);
  };

  onCancel = action((e: Event) => {
    const { categoryState, categoryState: { originalName }, props: { dataSource } } = this;

    categoryState.editMode = false;
    dataSource.updateName(originalName);
    dataSource.updateIsEditing(false);
  });

  updateDataSourceName = (e: Event) => {
    const name = e.target.value;
    const { dataSource } = this.props;
    dataSource.updateName(name);
  };

  getRouteLink = () => {
    const { routeParams, dataSource, routeObject } = this.props;
    const parseRouteParams = () => {
      let result = {};
      const keys = Object.keys(routeParams);
      keys.forEach(key => {
        result[key] =
          routeParams[key].dataKey === "reportId" || routeParams[key].dataKey === "dashboardId"
            ? null
            : routeParams[key].value || dataSource[routeParams[key].dataKey];
      });
      return result;
    };
    return routeUtils.getRouteUrl(routeObject, parseRouteParams());
  };

  handleClickLink = (e) => {
    if (e.button === 0)
      e.preventDefault();
    const { confirmDirtyForm, router } = this.props;
    const link = this.getRouteLink();

    confirmDirtyForm(uiState.isDirty, { onConfirmDirtyForm: uiState.onOkDirtyForm }).then(() => {
      router.push({
        pathname: link,
        state: {
          keepState: true
        }
      });
    });

    LayoutUtils.toggleSideBar(false);
    LayoutUtils.toggleIconMenu(false);
  };

  handleRenameClick = (e: Event) => {
    const { dataSource, getEditingCategoryTreeItem, onValidateCategoryName } = this.props;

    const enableEditMode = action(result => {
      if (isNil(result)) {
        e.persist();
        this.categoryState.editMode = true;
        dataSource.updateIsEditing(true);
      }
    });

    const editingCategoryTreeItem = getEditingCategoryTreeItem();
    if (editingCategoryTreeItem) {
      onValidateCategoryName(editingCategoryTreeItem).then(enableEditMode);
    } else {
      enableEditMode();
    }
  };

  loadReportList(catId: string) {
    const { loader, onLoadCategoryList } = this.props;

    loader.startLoading();
    return onLoadCategoryList && onLoadCategoryList(catId).finally(loader.endLoading.bind(loader));
  }

  renderEditModeName() {
    const { dataSource, dataSource: { isSubCategory, name }, onValidateCategoryName, localization } = this.props;

    const categoryConstraints = isSubCategory
      ? CATEGORY_TREE.CONSTRAINTS.SUB_CATEGORY_NAME
      : CATEGORY_TREE.CONSTRAINTS.CATEGORY_NAME;

    const nameValidation = merge(categoryConstraints, {
      action: () => {
        return onValidateCategoryName && onValidateCategoryName.bind(null, dataSource);
      }
    });

    return (
      <div className="title-edit form-group">
        <div className="input-group">
          <div>
            <ValidatorInput
              value={name}
              onChange={this.updateDataSourceName}
              onKeyPress={this.onEnter}
              onBlur={this.onSave}
              field="name"
              validations={nameValidation}
              className="form-control"
              autoFocus
            />
          </div>
          <span className="input-group-btn">
            <button
              className="btn btn-default"
              title={localization.getText("COMMON_TOOLTIP_UPDATE_CHANGE")}
              onMouseDown={this.onSave}
            >
              <span className="icon-ok success-color" />
            </button>
            <button
              className="btn btn-default"
              title={localization.getText("COMMON_TOOLTIP_CANCEL_CHANGE")}
              onMouseDown={this.onCancel}
            >
              <span className="icon-cancel" />
            </button>
          </span>
        </div>
      </div>
    );
  }

  renderReadonlyName() {
    const { dataSource: { name }, toggleIcon } = this.props;
    const toggleIconClassName = get("props.className", toggleIcon);
    let routeLink = this.getRouteLink();
    if (config.Integration) {
      routeLink = routeUtils.getPathForIntegration(routeLink);
    }

    return (
      <span>
        <OverflowText className="selectable" text={name} tooltip={name}>
          <a href={routeLink} onClick={this.handleClickLink}>
            {name}
          </a>
        </OverflowText>
        {toggleIcon && cloneElement(toggleIcon, { className: classnames(toggleIconClassName, "izenda-Arrow-Icon") })}
      </span>
    );
  }

  renderReadonlyActions() {
    const { isGlobalLocal, localization } = this.props;
    return (
      !isGlobalLocal &&
      <div className="pull-right">
        <i
          className="icon-pencil selectable"
          title={localization.getText("COMMON_TOOLTIP_RENAME")}
          onClick={this.handleRenameClick}
        />
      </div>
    );
  }

  render() {
    const { dataSource, localization } = this.props;
    const isHideReadonlyActions = this.categoryState.editMode || dataSource.isUneditable;

    return (
      <div>
        {this.categoryState.editMode ? this.renderEditModeName() : this.renderReadonlyName()}
        {!isHideReadonlyActions && this.renderReadonlyActions()}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/CategoryTree/Izenda.CategoryItem.js